import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  FiChevronDown,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiMaximize2,
  FiRefreshCw,
  FiSend,
  FiTrash2,
} from "react-icons/fi";
import {FaEraser, FaPaintBrush} from "react-icons/fa";
import {PixelGrid} from "../components/PixelGrid";
import {AdapterContext, ConnectionContext} from "../Contexts";
import {
  bitmapByteLength,
  countPixels,
  createBitmap,
  decodeBitmap,
  encodeBitmap,
} from "./images";

const mirrorModes = [
  {id: "none", label: "No mirror", icon: FiMaximize2},
  {id: "horizontal", label: "Left/right mirror", icon: FiChevronDown, transform: "rotate(90deg)"},
  {id: "vertical", label: "Up/down mirror", icon: FiChevronDown},
  {id: "quad", label: "Four-way mirror", icon: FiRefreshCw},
];

const sameBitmap = (a, b) => (
  a?.length === b?.length && a.every((value, index) => value === b[index])
);

const loadDraft = (adapter, profile) => {
  const expectedBytes = bitmapByteLength(profile);
  const profileDraft = localStorage.getItem(`pixel-draft:${adapter.id}:${profile.id}`);
  const legacyDraft = profile.id === "c3-48x12" ? localStorage.getItem("image") : null;
  return decodeBitmap(profileDraft || legacyDraft, expectedBytes) || createBitmap(profile);
};

function IconTool({label, active, icon, onClick, isDisabled, iconStyle}) {
  return (
    <Tooltip label={label} hasArrow>
      <IconButton
        aria-label={label}
        icon={icon}
        onClick={onClick}
        isDisabled={isDisabled}
        variant={active ? "solid" : "ghost"}
        colorScheme={active ? "studio" : "gray"}
        color={active ? "gray.900" : "gray.300"}
        size="sm"
        borderRadius="4px"
        sx={{svg: iconStyle}}
      />
    </Tooltip>
  );
}

export function PixelScreen() {
  const connected = useContext(ConnectionContext);
  const adapter = useContext(AdapterContext);
  const toast = useToast();
  const [profile, setProfile] = useState(() => adapter.getDisplayProfile());
  const [image, setImage] = useState(() => loadDraft(adapter, profile));
  const [tool, setTool] = useState("draw");
  const [mirror, setMirror] = useState("none");
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [encodedDraft, setEncodedDraft] = useState(() => encodeBitmap(image));
  const [invalidInput, setInvalidInput] = useState(false);
  const [sending, setSending] = useState(false);
  const strokeStart = useRef(null);

  const profiles = useMemo(() => adapter.listDisplayProfiles(), [adapter]);
  const litPixels = useMemo(() => countPixels(image, profile), [image, profile]);

  useEffect(() => {
    const detected = adapter.getDisplayProfile();
    setProfile((current) => current.id === detected.id ? current : detected);
  }, [adapter, connected]);

  useEffect(() => {
    const next = loadDraft(adapter, profile);
    setImage(next);
    setEncodedDraft(encodeBitmap(next));
    setInvalidInput(false);
    setUndoStack([]);
    setRedoStack([]);
  }, [adapter, profile]);

  useEffect(() => {
    const encoded = encodeBitmap(image);
    localStorage.setItem(`pixel-draft:${adapter.id}:${profile.id}`, encoded);
    setEncodedDraft(encoded);
  }, [adapter.id, image, profile.id]);

  const commit = (next) => {
    if (sameBitmap(image, next)) return;
    setUndoStack((items) => [...items.slice(-49), Uint8Array.from(image)]);
    setRedoStack([]);
    setImage(Uint8Array.from(next));
  };

  const undo = () => {
    if (!undoStack.length) return;
    const previous = undoStack[undoStack.length - 1];
    setUndoStack((items) => items.slice(0, -1));
    setRedoStack((items) => [...items, Uint8Array.from(image)]);
    setImage(Uint8Array.from(previous));
  };

  const redo = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((items) => items.slice(0, -1));
    setUndoStack((items) => [...items, Uint8Array.from(image)]);
    setImage(Uint8Array.from(next));
  };

  const send = async () => {
    setSending(true);
    try {
      await adapter.drawBitmap(image);
      toast({title: "Image sent", status: "success", duration: 1800});
    } catch (error) {
      toast({title: "Send failed", description: error.message, status: "error", duration: 4000});
    } finally {
      setSending(false);
    }
  };

  return (
    <Grid templateColumns={{base: "minmax(0, 1fr)", xl: "minmax(0, 1fr) 264px"}} gap={{base: 6, xl: 8}}>
      <Box minW={0}>
        <Flex align="center" justify="space-between" gap={3} mb={4} wrap="wrap">
          <HStack spacing={1} p="3px" bg="#111315" border="1px solid" borderColor="#2b2f34" borderRadius="5px">
            <IconTool label="Brush" active={tool === "draw"} icon={<FaPaintBrush />} onClick={() => setTool("draw")} />
            <IconTool label="Eraser" active={tool === "erase"} icon={<FaEraser />} onClick={() => setTool("erase")} />
            <Divider orientation="vertical" h="22px" borderColor="whiteAlpha.200" />
            {mirrorModes.map((mode) => (
              <IconTool
                key={mode.id}
                label={mode.label}
                active={mirror === mode.id}
                icon={<mode.icon />}
                iconStyle={mode.transform ? {transform: mode.transform} : undefined}
                onClick={() => setMirror(mode.id)}
              />
            ))}
          </HStack>
          <HStack spacing={1}>
            <IconTool label="Undo" icon={<FiCornerUpLeft />} onClick={undo} isDisabled={!undoStack.length} />
            <IconTool label="Redo" icon={<FiCornerUpRight />} onClick={redo} isDisabled={!redoStack.length} />
            <IconTool label="Clear canvas" icon={<FiTrash2 />} onClick={() => commit(createBitmap(profile))} />
          </HStack>
        </Flex>

        <Flex
          minH={{base: "250px", md: profile.height > 12 ? "540px" : "360px"}}
          align="center"
          justify="center"
          bg="#0e1012"
          border="1px solid"
          borderColor="#292d31"
          borderRadius="5px"
          p={{base: 4, md: 10}}
          overflowX="auto"
        >
          <PixelGrid
            bitmap={image}
            profile={profile}
            tool={tool}
            mirror={mirror}
            onChange={setImage}
            onStrokeStart={(start) => {
              strokeStart.current = Uint8Array.from(start);
            }}
            onStrokeEnd={(end) => {
              if (strokeStart.current && !sameBitmap(strokeStart.current, end)) {
                setUndoStack((items) => [...items.slice(-49), strokeStart.current]);
                setRedoStack([]);
              }
              strokeStart.current = null;
            }}
          />
        </Flex>

        <Flex mt={4} align="center" justify="space-between" gap={3} wrap="wrap">
          <HStack color="#7d838c" fontSize="11px" spacing={3}>
            <Text fontFamily="mono">{profile.width} × {profile.height}</Text>
            <Text fontFamily="mono">{litPixels} lit</Text>
            <Badge variant="subtle" colorScheme={connected ? "green" : "gray"}>
              {connected ? adapter.deviceName : "Draft"}
            </Badge>
          </HStack>
          <Button
            leftIcon={<FiSend />}
            colorScheme="studio"
            color="#17130a"
            onClick={send}
            isLoading={sending}
            isDisabled={!connected}
            borderRadius="5px"
          >
            Send to display
          </Button>
        </Flex>
      </Box>

      <Stack spacing={5} borderTop={{base: "1px solid", xl: 0}} borderLeft={{base: 0, xl: "1px solid"}} borderColor="#292d31" pt={{base: 5, xl: 0}} pl={{base: 0, xl: 6}}>
        <Box>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={3}>
            Display profile
          </Text>
          <FormControl>
            <FormLabel fontSize="sm" color="#8b9199">Matrix</FormLabel>
            <Select
              value={profile.id}
              onChange={(event) => {
                const next = adapter.setDisplayProfile(event.target.value);
                setProfile(next);
              }}
              isDisabled={connected}
              size="sm"
              borderRadius="5px"
            >
              {profiles.map((item) => (
                <option key={item.id} value={item.id}>{item.name} - {item.width} x {item.height}</option>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider borderColor="#292d31" />

        <Box>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={3}>
            Inspector
          </Text>
          <Stack spacing={3} fontSize="sm">
            <Flex justify="space-between"><Text color="#777d85">Tool</Text><Text>{tool === "draw" ? "Brush" : "Eraser"}</Text></Flex>
            <Flex justify="space-between"><Text color="#777d85">Symmetry</Text><Text>{mirrorModes.find((mode) => mode.id === mirror)?.label}</Text></Flex>
            <Flex justify="space-between"><Text color="#777d85">Payload</Text><Text fontFamily="mono">{image.length} B</Text></Flex>
          </Stack>
        </Box>

        <Divider borderColor="#292d31" />

        <Accordion allowToggle>
          <AccordionItem border="0">
            <AccordionButton px={0} _hover={{bg: "transparent"}}>
              <Box as="span" flex="1" textAlign="left" fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                Base64 data
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={0}>
              <Input
                value={encodedDraft}
                isInvalid={invalidInput}
                fontFamily="mono"
                fontSize="xs"
                onChange={(event) => {
                  const value = event.target.value;
                  setEncodedDraft(value);
                  const decoded = decodeBitmap(value, bitmapByteLength(profile));
                  setInvalidInput(!decoded);
                  if (decoded) commit(decoded);
                }}
                borderRadius="5px"
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Stack>
    </Grid>
  );
}
